import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ChevronRight,
  Star,
  Home,
  Hammer,
  Fence,
  Wrench,
  HardHat,
  CheckCircle,
  ArrowRight,
  Quote,
  Clock,
  Shield,
  Award,
  Grid3X3,
  PlusSquare
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility function
const cn = (...inputs) => twMerge(clsx(inputs));

// Company Info
const COMPANY = {
  name: "JP Home Restoration LLC",
  owners: "James Miller",
  phone: "(440) 858-6429",
  phoneRaw: "4408586429",
  email: "jphr@mailprint.net",
  location: "NE Ohio",
  logo: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/xpsyl2fc_2145.png"
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#about", label: "About" },
    { href: "#gallery", label: "Gallery" },
    { href: "#testimonials", label: "Testimonials" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-[#1A365D]/90 backdrop-blur-sm"
      )}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-28">
          <a href="#" className="flex items-center gap-4" data-testid="logo-link">
            <img 
              src={COMPANY.logo} 
              alt="JP Home Restoration Logo" 
              className="h-24 w-auto object-contain"
            />
            <div className="hidden md:block">
              <span className={cn(
                "font-['Chivo'] font-black text-2xl uppercase tracking-tight block",
                scrolled ? "text-[#1A365D]" : "text-white"
              )}>
                JP Home Restoration
              </span>
              <span className={cn(
                "text-sm font-bold tracking-widest uppercase",
                scrolled ? "text-[#2B6CB0]" : "text-[#63B3ED]"
              )}>
                LLC
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={cn(
                  "nav-link text-sm font-bold uppercase tracking-widest transition-colors",
                  scrolled ? "text-[#1A365D] hover:text-[#2B6CB0]" : "text-white/90 hover:text-white"
                )}
              >
                {link.label}
              </a>
            ))}
            <a
              href={`tel:+1${COMPANY.phoneRaw}`}
              data-testid="nav-call-btn"
              className="bg-[#2B6CB0] text-white h-12 px-6 rounded-sm font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-[#2C5282] transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              "lg:hidden p-2",
              scrolled ? "text-[#1A365D]" : "text-white"
            )}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-200"
          >
            <div className="container mx-auto px-4 py-6">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className="block py-3 text-[#1A365D] font-bold uppercase tracking-widest hover:text-[#2B6CB0] transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={`tel:+1${COMPANY.phoneRaw}`}
                className="mt-4 bg-[#2B6CB0] text-white h-12 px-6 rounded-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center">
      {/* Background Image - Using the addition project image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/52m36qu6_1719.jpg"
          alt="Home addition project"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A365D]/95 via-[#1A365D]/70 to-[#1A365D]/40"></div>
      </div>

      {/* Content */}
      <div className="relative container mx-auto px-4 md:px-8 max-w-7xl py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <span className="inline-block text-[#63B3ED] text-xs font-bold tracking-[0.2em] uppercase mb-4">
            Serving {COMPANY.location} • Quality Craftsmanship
          </span>
          <h1 className="font-['Chivo'] text-5xl md:text-7xl font-black tracking-tight leading-none uppercase text-white mb-6">
            Transforming Houses Into
            <span className="text-[#63B3ED]"> Dream Homes</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl leading-relaxed">
            From roofing and repairs to complete home renovations, decks, flooring, and additions — James Miller brings expert craftsmanship and attention to detail to every project in Northeast Ohio.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              data-testid="hero-quote-btn"
              className="btn-primary bg-[#2B6CB0] text-white h-14 px-10 rounded-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2 hover:-translate-y-1 transition-transform shadow-lg hover:bg-[#2C5282]"
            >
              Get a Free Quote
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href={`tel:+1${COMPANY.phoneRaw}`}
              data-testid="hero-call-btn"
              className="border-2 border-white text-white h-14 px-10 rounded-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-white hover:text-[#1A365D] transition-colors"
            >
              <Phone className="w-5 h-5" />
              {COMPANY.phone}
            </a>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/20">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-[#63B3ED]" />
              <div>
                <p className="text-white font-bold">Licensed & Insured</p>
                <p className="text-white/60 text-sm">Full coverage</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Award className="w-8 h-8 text-[#63B3ED]" />
              <div>
                <p className="text-white font-bold">Quality Guaranteed</p>
                <p className="text-white/60 text-sm">100% satisfaction</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-[#63B3ED]" />
              <div>
                <p className="text-white font-bold">On-Time Delivery</p>
                <p className="text-white/60 text-sm">We meet deadlines</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const services = [
  {
    icon: Home,
    title: "Roofing",
    description: "Complete roof installation, repair, and replacement services. We work with all materials including shingles and metal roofing systems."
  },
  {
    icon: Wrench,
    title: "Repairs",
    description: "General home repairs from minor fixes to major structural work. No job is too small or too large for our skilled team."
  },
  {
    icon: Fence,
    title: "Decks",
    description: "Custom deck design and construction. Transform your outdoor space with beautiful, durable decking and dock solutions."
  },
  {
    icon: Hammer,
    title: "Home Renovation",
    description: "Breathe new life into aging homes. We specialize in transforming old, tired properties into vibrant, modern living spaces."
  },
  {
    icon: Grid3X3,
    title: "Flooring",
    description: "Professional flooring installation including hardwood, laminate, tile, and vinyl. Beautiful floors that last for years."
  },
  {
    icon: PlusSquare,
    title: "Additions",
    description: "Expand your living space with custom home additions. From sunrooms to full room additions, we handle it all."
  },
  {
    icon: HardHat,
    title: "General Construction",
    description: "Full-service construction for remodels, outbuildings, and new builds. Professional results from foundation to finish."
  }
];

const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-gray-50">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#2B6CB0] text-xs font-bold tracking-[0.2em] uppercase">
            What We Do
          </span>
          <h2 className="font-['Chivo'] text-3xl md:text-5xl font-bold tracking-tight mt-2 mb-4 text-[#1A365D]">
            Our Services
          </h2>
          <div className="w-20 h-1 bg-[#2B6CB0] mx-auto"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="service-card group relative overflow-hidden bg-white border border-gray-200 p-8 hover:shadow-xl transition-shadow duration-300 hover:border-[#2B6CB0]/50"
            >
              <div className="service-icon w-16 h-16 bg-gray-100 rounded-sm flex items-center justify-center mb-6 transition-all duration-300 group-hover:bg-[#2B6CB0]/10">
                <service.icon className="w-8 h-8 text-[#1A365D] group-hover:text-[#2B6CB0] transition-colors" />
              </div>
              <h3 className="font-['Chivo'] text-xl md:text-2xl font-bold tracking-wide mb-3 text-[#1A365D]">
                {service.title}
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                {service.description}
              </p>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-[#2B6CB0] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  const features = [
    "Licensed and insured professionals",
    "Free detailed estimates",
    "Quality materials and workmanship",
    "On-time project completion",
    "Clean and respectful work sites",
    "Customer satisfaction guaranteed"
  ];

  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#2B6CB0] text-xs font-bold tracking-[0.2em] uppercase">
              About Us
            </span>
            <h2 className="font-['Chivo'] text-3xl md:text-5xl font-bold tracking-tight mt-2 mb-6 text-[#1A365D]">
              Building Trust Through Quality Work
            </h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              At JP Home Restoration LLC, James Miller believes every home tells a story. Our mission is to help you write the next chapter by transforming aging properties into beautiful, functional living spaces.
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Serving Northeast Ohio with years of experience in roofing, repairs, decks, flooring, additions, and complete home renovations, our team brings expertise and passion to every project—big or small.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#2B6CB0] flex-shrink-0" />
                  <span className="text-[#1A365D] font-medium">{feature}</span>
                </div>
              ))}
            </div>

            <a
              href="#contact"
              data-testid="about-cta-btn"
              className="inline-flex items-center gap-2 bg-[#1A365D] text-white h-12 px-8 rounded-sm font-bold uppercase tracking-widest hover:bg-[#2C5282] transition-colors"
            >
              Start Your Project
              <ChevronRight className="w-5 h-5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/ar0q9h3t_1656.jpg"
                alt="Metal roofing project"
                className="w-full h-[500px] object-cover rounded-sm"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#2B6CB0] text-white p-6 rounded-sm">
                <p className="font-['Chivo'] text-4xl font-black">NE Ohio</p>
                <p className="font-bold uppercase tracking-widest text-sm">Locally Owned</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Gallery Section with real project photos
const galleryImages = [
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/vuv1shdz_1857.jpg",
    title: "Home Addition - Before",
    category: "Addition",
    type: "before"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/52m36qu6_1719.jpg",
    title: "Home Addition - After",
    category: "Addition",
    type: "after"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/iuqribo7_1717.jpg",
    title: "Dock Repair - Before",
    category: "Deck/Dock",
    type: "before"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/qtqvvb81_1718.jpg",
    title: "Dock Rebuild - After",
    category: "Deck/Dock",
    type: "after"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/ar0q9h3t_1656.jpg",
    title: "Commercial Metal Roofing",
    category: "Roofing"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/04rfpshy_1854.jpg",
    title: "Siding Repair",
    category: "Repairs"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/j4yshhg1_1666.jpg",
    title: "Custom Storage Shed",
    category: "Construction"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/noqzj3gw_1665.jpg",
    title: "Storage Shed Build",
    category: "Construction"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/4byx6efq_1661.jpg",
    title: "Custom Staircase",
    category: "Interior"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/myaatnko_1660.jpg",
    title: "Staircase & Railing",
    category: "Interior"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_jp-restoration-hub/artifacts/76uez1al_1659.jpg",
    title: "Flooring Installation",
    category: "Flooring"
  }
];

const Gallery = () => {
  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 md:py-32 bg-[#1A365D]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#63B3ED] text-xs font-bold tracking-[0.2em] uppercase">
            Our Work
          </span>
          <h2 className="font-['Chivo'] text-3xl md:text-5xl font-bold tracking-tight mt-2 mb-4 text-white">
            Project Gallery
          </h2>
          <div className="w-20 h-1 bg-[#63B3ED] mx-auto"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {galleryImages.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`gallery-item-${index}`}
              className={cn(
                "gallery-item relative group cursor-pointer overflow-hidden rounded-sm",
                index === 0 ? "md:col-span-2 md:row-span-2" : ""
              )}
            >
              <img
                src={image.url}
                alt={image.title}
                className={cn(
                  "w-full object-cover transition-transform duration-500 group-hover:scale-110",
                  index === 0 ? "h-[400px] md:h-full min-h-[500px]" : "h-[300px]"
                )}
              />
              <div className="absolute inset-0 bg-[#1A365D]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <div>
                  {image.type && (
                    <span className={cn(
                      "inline-block px-2 py-1 text-xs font-bold uppercase tracking-wider mb-2 rounded-sm",
                      image.type === "before" ? "bg-gray-500 text-white" : "bg-green-500 text-white"
                    )}>
                      {image.type}
                    </span>
                  )}
                  <span className="block text-[#63B3ED] text-xs font-bold tracking-widest uppercase">
                    {image.category}
                  </span>
                  <h3 className="font-['Chivo'] text-xl font-bold text-white mt-1">
                    {image.title}
                  </h3>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const testimonials = [
  {
    name: "Robert M.",
    location: "Akron, OH",
    text: "JP Home Restoration LLC transformed our 50-year-old house into a modern dream home. The attention to detail and professionalism exceeded our expectations.",
    rating: 5
  },
  {
    name: "Sarah L.",
    location: "Canton, OH",
    text: "The roofing team was fantastic. They completed the job ahead of schedule and cleaned up perfectly. Highly recommend for anyone in Northeast Ohio!",
    rating: 5
  },
  {
    name: "Michael T.",
    location: "Cleveland, OH",
    text: "Our new deck is absolutely stunning. The craftsmanship is top-notch, and James was a pleasure to work with throughout the project.",
    rating: 5
  }
];

const Testimonials = () => {
  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#2B6CB0] text-xs font-bold tracking-[0.2em] uppercase">
            Testimonials
          </span>
          <h2 className="font-['Chivo'] text-3xl md:text-5xl font-bold tracking-tight mt-2 mb-4 text-[#1A365D]">
            What Our Clients Say
          </h2>
          <div className="w-20 h-1 bg-[#2B6CB0] mx-auto"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              data-testid={`testimonial-card-${index}`}
              className="testimonial-card bg-gray-50 p-8 rounded-sm border-l-4 border-[#2B6CB0] relative"
            >
              <Quote className="w-10 h-10 text-[#2B6CB0]/20 absolute top-6 right-6" />
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#2B6CB0] text-[#2B6CB0]" />
                ))}
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">
                "{testimonial.text}"
              </p>
              <div>
                <p className="font-bold text-[#1A365D]">{testimonial.name}</p>
                <p className="text-sm text-gray-500">{testimonial.location}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section with Quote Form
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/quote-requests`, formData);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (error) {
      setSubmitStatus("error");
      console.error("Error submitting quote request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-gray-50">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#2B6CB0] text-xs font-bold tracking-[0.2em] uppercase">
              Get In Touch
            </span>
            <h2 className="font-['Chivo'] text-3xl md:text-5xl font-bold tracking-tight mt-2 mb-6 text-[#1A365D]">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Contact James Miller today for a free, no-obligation estimate. We'll discuss your vision and provide expert recommendations for your home restoration project.
            </p>

            <div className="space-y-6 mb-8">
              <a href={`tel:+1${COMPANY.phoneRaw}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                <div className="w-14 h-14 bg-[#2B6CB0] rounded-sm flex items-center justify-center group-hover:bg-[#2C5282] transition-colors">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-widest">Phone</p>
                  <p className="text-xl font-bold text-[#1A365D]">{COMPANY.phone}</p>
                </div>
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                <div className="w-14 h-14 bg-[#1A365D] rounded-sm flex items-center justify-center group-hover:bg-[#2C5282] transition-colors">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-widest">Email</p>
                  <p className="text-xl font-bold text-[#1A365D]">{COMPANY.email}</p>
                </div>
              </a>
              <div className="mt-4 p-4 bg-[#2B6CB0]/10 border-l-4 border-[#2B6CB0] rounded-sm">
                <p className="text-[#1A365D] font-medium">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Prefer a quick response? James prefers texts and calls over emails!
                </p>
              </div>
              <div className="flex items-center gap-4" data-testid="contact-address">
                <div className="w-14 h-14 bg-[#1A365D] rounded-sm flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-widest">Service Area</p>
                  <p className="text-xl font-bold text-[#1A365D]">{COMPANY.location}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Quote Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} data-testid="quote-form" className="bg-white p-8 shadow-xl rounded-sm">
              <h3 className="font-['Chivo'] text-2xl font-bold mb-6 text-[#1A365D]">
                Request a Free Quote
              </h3>

              {submitStatus === "success" && (
                <div data-testid="form-success-message" className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-sm">
                  Thank you! We'll contact you shortly with your free quote.
                </div>
              )}

              {submitStatus === "error" && (
                <div data-testid="form-error-message" className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-sm">
                  Something went wrong. Please try again or call us directly.
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-bold uppercase text-gray-500 mb-2 block">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="quote-form-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="form-input w-full h-12 bg-gray-50 border border-gray-200 px-4 rounded-sm focus:border-[#2B6CB0] focus:ring-1 focus:ring-[#2B6CB0] transition-all outline-none"
                    placeholder="John Smith"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-bold uppercase text-gray-500 mb-2 block">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      data-testid="quote-form-email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="form-input w-full h-12 bg-gray-50 border border-gray-200 px-4 rounded-sm focus:border-[#2B6CB0] focus:ring-1 focus:ring-[#2B6CB0] transition-all outline-none"
                      placeholder="john@email.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold uppercase text-gray-500 mb-2 block">
                      Phone
                    </label>
                    <input
                      type="tel"
                      data-testid="quote-form-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="form-input w-full h-12 bg-gray-50 border border-gray-200 px-4 rounded-sm focus:border-[#2B6CB0] focus:ring-1 focus:ring-[#2B6CB0] transition-all outline-none"
                      placeholder="(440) 555-1234"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold uppercase text-gray-500 mb-2 block">
                    Service Needed *
                  </label>
                  <select
                    required
                    data-testid="quote-form-service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="form-input w-full h-12 bg-gray-50 border border-gray-200 px-4 rounded-sm focus:border-[#2B6CB0] focus:ring-1 focus:ring-[#2B6CB0] transition-all outline-none"
                  >
                    <option value="">Select a service</option>
                    <option value="roofing">Roofing</option>
                    <option value="repairs">Repairs</option>
                    <option value="decks">Decks</option>
                    <option value="flooring">Flooring</option>
                    <option value="additions">Additions</option>
                    <option value="renovation">Home Renovation</option>
                    <option value="construction">General Construction</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-bold uppercase text-gray-500 mb-2 block">
                    Project Details *
                  </label>
                  <textarea
                    required
                    data-testid="quote-form-message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="form-input w-full bg-gray-50 border border-gray-200 p-4 rounded-sm focus:border-[#2B6CB0] focus:ring-1 focus:ring-[#2B6CB0] transition-all resize-none outline-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="quote-form-submit"
                  className="w-full btn-primary bg-[#2B6CB0] text-white h-14 px-8 rounded-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-[#2C5282] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Submitting..." : "Get My Free Quote"}
                  {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#1A365D] text-white py-16">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={COMPANY.logo} 
                alt="JP Home Restoration LLC Logo" 
                className="h-24 w-auto object-contain bg-white p-2 rounded-sm"
              />
            </div>
            <p className="text-white/70 leading-relaxed mb-4">
              Owned and operated by James Miller. Transforming houses into dream homes with quality craftsmanship and attention to detail throughout Northeast Ohio.
            </p>
            <p className="text-white/60 leading-relaxed mb-6">
              Licensed, insured, and committed to excellence.
            </p>
            <div className="flex gap-4">
              <a href={`tel:+1${COMPANY.phoneRaw}`} data-testid="footer-call-btn" className="bg-[#2B6CB0] text-white h-12 px-6 rounded-sm font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-[#63B3ED] transition-colors">
                <Phone className="w-4 h-4" />
                Call Now
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Chivo'] font-bold uppercase tracking-widest mb-4 text-[#63B3ED]">Services</h4>
            <ul className="space-y-2">
              {["Roofing", "Repairs", "Decks", "Flooring", "Additions", "Home Renovation", "General Construction"].map((service) => (
                <li key={service}>
                  <a href="#services" className="text-white/60 hover:text-[#63B3ED] transition-colors">
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Chivo'] font-bold uppercase tracking-widest mb-4 text-[#63B3ED]">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:+1${COMPANY.phoneRaw}`} className="flex items-center gap-2 text-white/60 hover:text-[#63B3ED] transition-colors">
                  <Phone className="w-4 h-4" />
                  {COMPANY.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 text-white/60 hover:text-[#63B3ED] transition-colors">
                  <Mail className="w-4 h-4" />
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-center gap-2 text-white/60">
                <MapPin className="w-4 h-4" />
                {COMPANY.location}
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </p>
          <p className="text-white/40 text-sm">
            Owned by {COMPANY.owners} | Licensed & Insured
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Navigation />
      <Hero />
      <Services />
      <About />
      <Gallery />
      <Testimonials />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
